import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectService } from '@/services/projectService';
import { useTheme } from '@/hooks/useTheme';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { useEffect, useRef, useState } from 'react';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { getProjectLogoUrl } from '@/utils';
import { Toast } from '@/components/Toast';

const projectSchema = z.object({
  title: z.string().min(3, 'Le titre doit contenir au moins 3 caractères').max(255, 'Le titre est trop long'),
  code: z
    .string()
    .max(64, 'Le code est trop long')
    .regex(/^[A-Za-z0-9][A-Za-z0-9_-]*$/, 'Le code doit contenir uniquement des lettres/chiffres et les caractères - ou _')
    .optional()
    .or(z.literal('')),
  adresse: z.string().max(500, 'L\'adresse est trop longue').optional().or(z.literal('')),
  city: z.string().max(255, 'La ville est trop longue').optional().or(z.literal('')),
});

type ProjectFormData = z.infer<typeof projectSchema>;

export function EditProjectPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isDark, toggleTheme } = useTheme();
  const { user, isSuperRoot } = useAuthStore();
  const { logout } = useAuth();

  // Fetch project data
  const { data: project, isLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: () => projectService.getProject(Number(id)),
    enabled: !!id,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [showToast, setShowToast] = useState(false);
  const [deleteStep, setDeleteStep] = useState<0 | 1 | 2>(0);
  const [deleteConfirmName, setDeleteConfirmName] = useState<string>('');

  // Reset form when project data is loaded
  useEffect(() => {
    if (project) {
      reset({
        title: project.title,
        code: project.code || '',
        adresse: project.adresse || '',
        city: project.city || '',
      });
      // Set logo preview if exists
      if (project.logo) {
        const logoUrl = getProjectLogoUrl(project.logo);
        setLogoPreview(logoUrl);
      } else {
        setLogoPreview(null);
      }
    }
  }, [project, reset]);

  const updateMutation = useMutation({
    mutationFn: (data: ProjectFormData) => projectService.updateProject(Number(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      setToastMessage('Projet modifié avec succès !');
      setToastType('success');
      setShowToast(true);
      setTimeout(() => {
        navigate('/admin/dashboard');
      }, 1500);
    },
    onError: () => {
      setToastMessage('Erreur lors de la modification du projet');
      setToastType('error');
      setShowToast(true);
    },
  });

  const uploadLogoMutation = useMutation({
    mutationFn: (file: File) => projectService.uploadLogo(Number(id), file),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      // Update preview
      if (data.logo) {
        const logoUrl = getProjectLogoUrl(data.logo);
        setLogoPreview(logoUrl);
      }
    },
    onError: (error: any) => {
      console.error('Upload error:', error);
      // Error message will be displayed in the UI via uploadLogoMutation.isError
    },
  });

  const deleteLogoMutation = useMutation({
    mutationFn: () => projectService.deleteLogo(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setLogoPreview(null);
    },
  });

  const deepDeleteMutation = useMutation({
    mutationFn: () => projectService.deepDeleteProject(Number(id), project.title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setToastMessage('Projet supprimé avec succès.');
      setToastType('success');
      setShowToast(true);
      setTimeout(() => {
        navigate('/admin/dashboard');
      }, 1200);
    },
    onError: (error: any) => {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        'Erreur lors de la suppression du projet';
      setToastMessage(String(msg));
      setToastType('error');
      setShowToast(true);
    },
  });

  const onSubmit = (data: ProjectFormData) => {
    updateMutation.mutate(data);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        alert('Type de fichier non autorisé. Formats acceptés : JPEG, PNG, GIF, WebP, SVG');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        alert('Le fichier est trop volumineux. Taille maximale : 5MB');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Upload file
      uploadLogoMutation.mutate(file);
    }
  };

  const handleDeleteLogo = () => {
    if (confirm('Êtes-vous sûr de vouloir supprimer le logo ?')) {
      deleteLogoMutation.mutate();
    }
  };

  const openDeleteModal = () => {
    setDeleteConfirmName('');
    setDeleteStep(1);
  };

  const closeDeleteModal = () => {
    if (deepDeleteMutation.isPending) return;
    setDeleteStep(0);
    setDeleteConfirmName('');
  };

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-slate-500 dark:text-slate-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <p className="text-red-500">Projet non trouvé</p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="mt-4 text-primary hover:text-primary-600"
          >
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      {/* Sidebar */}
      <AdminSidebar getUserInitials={getUserInitials} />

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        {/* Header */}
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="flex items-center gap-4 md:hidden">
            <button className="text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-white">
              <span className="material-icons-outlined">menu</span>
            </button>
            <span className="font-bold text-lg dark:text-white">GTB Admin</span>
          </div>
          <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
            <span className="text-slate-400 dark:text-slate-600">Admin</span> / Projets / Éditer
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? (
                <span className="material-icons-outlined">light_mode</span>
              ) : (
                <span className="material-icons-outlined">dark_mode</span>
              )}
            </button>
            <button className="relative p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors">
              <span className="material-icons-outlined">notifications</span>
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8 flex items-center justify-between">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="inline-flex items-center text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
              >
                <span className="material-icons-outlined text-lg mr-1">arrow_back</span>
                Retour à la liste
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate(`/admin/projects/${id}/members`)}
                  className="inline-flex items-center px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-[#16202A] hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <span className="material-icons-outlined text-lg mr-2">people</span>
                  Gérer les membres
                </button>
                <button
                  onClick={() => navigate(`/admin/projects/${id}/areas`)}
                  className="inline-flex items-center px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-[#16202A] hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <span className="material-icons-outlined text-lg mr-2">layers</span>
                  Gérer les zones
                </button>
                <button
                  onClick={() => navigate(`/admin/projects/${id}/equipments`)}
                  className="inline-flex items-center px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-[#16202A] hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <span className="material-icons-outlined text-lg mr-2">router</span>
                  Gérer les équipements
                </button>
              </div>
            </div>

            <div className="bg-white dark:bg-[#16202A] shadow-sm rounded-xl border border-slate-200 dark:border-[#1E293B] overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-[#1E293B]">
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Éditer le Projet</h1>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Modifiez les informations du projet "{project.title}".
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="p-6">
                {updateMutation.isError && (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-center gap-2 text-red-800 dark:text-red-400">
                      <span className="material-icons-outlined">error</span>
                      <p className="text-sm font-medium">
                        Erreur lors de la modification du projet. Veuillez réessayer.
                      </p>
                    </div>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Title Field */}
                  <div>
                    <label
                      htmlFor="title"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                    >
                      Titre du projet <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="title"
                      type="text"
                      {...register('title')}
                      className="block w-full px-4 py-3 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                      placeholder="Ex: Siège Social Paris"
                    />
                    {errors.title && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.title.message}</p>
                    )}
                  </div>

                  {/* Code Field */}
                  <div>
                    <label
                      htmlFor="code"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                    >
                      Code projet
                    </label>
                    <input
                      id="code"
                      type="text"
                      {...register('code')}
                      className="block w-full px-4 py-3 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                      placeholder="Ex: SMALT_PARIS_01"
                    />
                    {errors.code && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.code.message}</p>
                    )}
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Optionnel. Lettres/chiffres + <code className="font-mono">-</code> ou <code className="font-mono">_</code>.
                    </p>
                  </div>

                  {/* Address Field */}
                  <div>
                    <label
                      htmlFor="adresse"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                    >
                      Adresse
                    </label>
                    <input
                      id="adresse"
                      type="text"
                      {...register('adresse')}
                      className="block w-full px-4 py-3 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                      placeholder="Ex: 12 Rue de la Paix"
                    />
                    {errors.adresse && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.adresse.message}</p>
                    )}
                  </div>

                  {/* City Field */}
                  <div>
                    <label
                      htmlFor="city"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                    >
                      Ville
                    </label>
                    <input
                      id="city"
                      type="text"
                      {...register('city')}
                      className="block w-full px-4 py-3 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                      placeholder="Ex: Paris"
                    />
                    {errors.city && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.city.message}</p>
                    )}
                  </div>

                  {/* Logo Upload Field */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Logo du projet
                    </label>
                    <div className="space-y-4">
                      {logoPreview && (
                        <div className="relative inline-block">
                          <img
                            src={logoPreview}
                            alt="Logo preview"
                            className="h-32 w-32 object-contain border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] p-2"
                          />
                          <button
                            type="button"
                            onClick={handleDeleteLogo}
                            disabled={deleteLogoMutation.isPending}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                            title="Supprimer le logo"
                          >
                            <span className="material-icons-outlined text-sm">close</span>
                          </button>
                        </div>
                      )}
                      <div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadLogoMutation.isPending}
                          className="inline-flex items-center px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-[#16202A] hover:bg-slate-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {uploadLogoMutation.isPending ? (
                            <>
                              <svg
                                className="animate-spin -ml-1 mr-2 h-4 w-4 text-slate-500"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              Upload en cours...
                            </>
                          ) : (
                            <>
                              <span className="material-icons-outlined text-lg mr-2">upload</span>
                              {logoPreview ? 'Changer le logo' : 'Télécharger un logo'}
                            </>
                          )}
                        </button>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                          Formats acceptés : JPEG, PNG, GIF, WebP, SVG (max 5MB)
                        </p>
                      </div>
                    </div>
                    {uploadLogoMutation.isError && (
                      <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p className="text-sm font-medium text-red-800 dark:text-red-400">
                          Erreur lors de l'upload du logo
                        </p>
                        <p className="mt-1 text-xs text-red-600 dark:text-red-500">
                          {(uploadLogoMutation.error as any)?.response?.data?.error || 
                           (uploadLogoMutation.error as any)?.response?.data?.message ||
                           (uploadLogoMutation.error as any)?.message ||
                           'Une erreur inattendue s\'est produite. Veuillez réessayer.'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Info Box */}
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex gap-3">
                      <span className="material-icons-outlined text-blue-600 dark:text-blue-400 flex-shrink-0">
                        info
                      </span>
                      <div>
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                          Modification du projet
                        </p>
                        <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                          Les modifications seront appliquées immédiatement. Les zones, boxes et utilisateurs associés
                          ne seront pas affectés.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Danger Zone — réservée aux utilisateurs ROLE_SUPER_ROOT */}
                  {isSuperRoot() && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex gap-3">
                          <span className="material-icons-outlined text-red-600 dark:text-red-400 flex-shrink-0">
                            warning
                          </span>
                          <div>
                            <p className="text-sm font-medium text-red-900 dark:text-red-300">
                              Zone de danger
                            </p>
                            <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                              Supprime définitivement le projet, ses zones, équipements, attributs, historiques, documents et fichiers associés.
                              Les utilisateurs ne sont jamais supprimés (seuls les liens au projet le sont).
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={openDeleteModal}
                          className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
                        >
                          <span className="material-icons-outlined text-lg mr-2">delete</span>
                          Supprimer le projet
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end gap-3 pt-6 border-t border-slate-200 dark:border-[#1E293B]">
                    <button
                      type="button"
                      onClick={() => navigate('/admin/dashboard')}
                      className="px-6 py-2.5 border border-slate-300 dark:border-[#1E293B] rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-[#16202A] hover:bg-slate-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={updateMutation.isPending}
                      className="inline-flex items-center px-6 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-primary hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {updateMutation.isPending ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Modification en cours...
                        </>
                      ) : (
                        <>
                          <span className="material-icons-outlined text-lg mr-2">save</span>
                          Enregistrer les modifications
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>

      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          isVisible={showToast}
          onClose={() => setShowToast(false)}
        />
      )}

      {/* Deep Delete Modal - Step 1: type project name */}
      {deleteStep === 1 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 max-w-md w-full border border-slate-200 dark:border-slate-700">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Supprimer le projet ?</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              Pour confirmer, tape exactement le nom du projet :
              <span
                className="block mt-2 font-semibold text-slate-900 dark:text-white select-none"
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                onContextMenu={(e) => e.preventDefault()}
              >
                "{project.title}"
              </span>
            </p>
            <input
              type="text"
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              className="block w-full px-4 py-3 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
              placeholder="Nom du projet"
              autoFocus
            />
            <div className="flex gap-3 justify-end mt-5">
              <button
                type="button"
                onClick={closeDeleteModal}
                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => setDeleteStep(2)}
                disabled={deleteConfirmName.trim() !== project.title}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continuer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deep Delete Modal - Step 2: final confirmation */}
      {deleteStep === 2 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 max-w-md w-full border border-slate-200 dark:border-slate-700">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Confirmation finale</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              Cette action est irréversible. Le projet "{project.title}" sera définitivement supprimé ainsi que toutes ses données.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={deepDeleteMutation.isPending}
                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => deepDeleteMutation.mutate()}
                disabled={deepDeleteMutation.isPending}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deepDeleteMutation.isPending ? 'Suppression...' : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
